import React from 'react'
import { CreditCard } from 'lucide-react'

export default function StripeTab({ stripeData, stripeLoading, webhookEvents, refreshWebhooks }) {
  return (
    <div className="space-y-6">
      {stripeLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        </div>
      ) : stripeData?.balance ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-zinc-800 rounded-xl p-5 border">
              <p className="text-sm text-zinc-400">Available Balance</p>
              <p className="text-2xl font-bold text-emerald-400">
                ${(stripeData.balance.available || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-zinc-800 rounded-xl p-5 border">
              <p className="text-sm text-zinc-400">Pending Balance</p>
              <p className="text-2xl font-bold text-amber-600">
                ${(stripeData.balance.pending || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-zinc-800 rounded-xl p-5 border">
              <p className="text-sm text-zinc-400">Success Rate</p>
              <p className="text-2xl font-bold text-green-500">
                {(stripeData.metrics?.success_rate || 0).toFixed(1)}%
              </p>
            </div>
            <div className="bg-zinc-800 rounded-xl p-5 border">
              <p className="text-sm text-zinc-400">Active Subscriptions</p>
              <p className="text-2xl font-bold text-purple-400">
                {stripeData.metrics?.active_subscriptions || 0}
              </p>
            </div>
          </div>

          <div className="bg-zinc-800 rounded-2xl p-6 border">
            <h3 className="font-semibold mb-4">Recent Stripe Charges</h3>
            <div className="space-y-3">
              {stripeData.recent_charges?.slice(0, 10).map((c) => (
                <div key={c.id} className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                  <div>
                    <p className="font-medium">${(c.amount || 0).toFixed(2)} {c.currency?.toUpperCase()}</p>
                    <p className="text-sm text-zinc-400">{c.description || 'No description'}</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      c.paid ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {c.paid ? 'Paid' : c.status}
                    </span>
                    <p className="text-xs text-zinc-500 mt-1">
                      {c.created ? new Date(c.created).toLocaleDateString() : ''}
                    </p>
                  </div>
                </div>
              ))}
              {(!stripeData.recent_charges || stripeData.recent_charges.length === 0) && (
                <p className="text-zinc-400 text-center py-4">No recent charges</p>
              )}
            </div>
          </div>

          {/* Webhook Events Section */}
          <div className="bg-zinc-800 rounded-2xl p-6 border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Recent Webhook Events</h3>
              <button 
                onClick={() => refreshWebhooks()}
                className="text-sm text-green-500 hover:text-green-400"
              >
                Refresh
              </button>
            </div>
            <div className="space-y-2">
              {webhookEvents?.events?.slice(0, 15).map((event, idx) => (
                <div key={event.event_id || idx} className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full ${
                      event.type?.includes('failed') ? 'bg-red-500' :
                      event.type?.includes('deleted') || event.type?.includes('deauthorized') ? 'bg-amber-500' :
                      'bg-green-500/100'
                    }`}></span>
                    <div>
                      <p className="font-mono text-sm">{event.type}</p>
                      <p className="text-xs text-zinc-400">
                        {event.processed_at ? new Date(event.processed_at).toLocaleString() : ''}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-zinc-500 font-mono">
                    {event.event_id?.slice(0, 20)}...
                  </span>
                </div>
              ))}
              {(!webhookEvents?.events || webhookEvents.events.length === 0) && (
                <div className="text-center py-6 text-zinc-400">
                  <p>No webhook events received yet</p>
                  <p className="text-xs mt-1">Events will appear here when Stripe sends webhooks</p>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 text-center">
          <p className="text-yellow-800">Stripe is not configured or unavailable</p>
          <p className="text-sm text-yellow-400 mt-1">Check your Stripe API keys in the backend configuration</p>
        </div>
      )}
    </div>
  )
}
